import React from 'react';
import { TextInput } from 'lego-on-react';
import { TextInputProps, TextInputSuggestsMeta } from 'lego-on-react-custom-types';
import debounce from 'lodash/debounce';

import { getHostsForSuggest } from '../../api';
import { Field } from '../Form/Form';
import { SuggestLoading } from '../Suggest/SuggestLoading';
import { HostItem } from './HostItem';

interface Props {
    onHostChange: (value: string) => void;
    disabled: boolean;
    verificationError?: string;
    required?: boolean;
    error?: string;

    name: string;
    label: string;
    caption?: React.ReactNode;
    sideCaption?: React.ReactNode;
    documentationUrl?: string;
    text?: string;
    id?: string;
}

interface State {
    query: string;
    isFetching: boolean;
}

export default class OrganizationsSelect extends React.Component<Props, State> {
    public state: State = {
        query: this.props.text || '',
        isFetching: false,
    };

    private lastRequest: string[] = [];
    private cachedRequests: Record<string, string[]> = {};
    private dataProvider = debounce(
        async (suggestUrl, val, callback) => {
            const hosts = await this.fetchSuggest(val);

            if (!hosts || val !== this.state.query) {
                return;
            }

            callback(makeSuggestItems(hosts));
        },
        200,
        { trailing: true },
    );

    public render() {
        return (
            <>
                <Field
                    name={this.props.name}
                    label={this.props.label}
                    verificationError={this.props.verificationError}
                    required={this.props.required}
                    caption={this.props.caption}
                    sideCaption={this.props.sideCaption}
                    documentationUrl={this.props.documentationUrl}
                    error={this.props.error}
                >
                    <TextInput
                        id={this.props.id}
                        name={this.props.name}
                        theme="normal"
                        size="s"
                        type="url"
                        text={this.state.query}
                        onChange={this.onChange}
                        autocomplete={false}
                        suggest
                        dataprovider={this.dataProvider}
                        disabled={this.props.disabled}
                    />
                    {this.state.isFetching && <SuggestLoading />}
                </Field>
            </>
        );
    }

    private async fetchSuggest(val: string): Promise<string[] | null> {
        if (this.state.isFetching) {
            return null;
        }
        this.setState({ isFetching: true });

        let hosts: string[] = [];
        try {
            hosts = this.cachedRequests[val] || (await getHostsForSuggest({ query: val }));

            this.lastRequest = hosts;
            this.cachedRequests[val] = hosts;
        } catch (e) {
            console.error(e);
        }

        this.setState({ isFetching: false });

        return hosts;
    }

    private onChange = (value: string | JSX.Element, ...args: unknown[]) => {
        // Недокументированная функциональность TextInput в lego-on-react
        const [, meta] = args as [TextInputProps, TextInputSuggestsMeta | undefined];
        const query = typeof value === 'string' ? value : value.props.host;

        if (!this.lastRequest) {
            this.setState({ query });
            return;
        }

        if (typeof value === 'string') {
            this.setHost(query);
        } else if (meta && meta.source === 'suggest' && typeof meta.itemIndex !== 'number') {
            this.setHost(value.props.host);
        }
    };

    private setHost = (host: string) => {
        this.props.onHostChange(host);
        this.setState({ query: host });
    };
}

function makeSuggestItems(hosts: string[]) {
    return hosts.map((host, i) => <HostItem key={i} host={host} />);
}
